/**
 * Progress.jsx — v2.5 Unified Home + Progress + Groups
 *
 * Replaces both Dashboard.jsx and the old Progress.jsx.
 * Route: /progress  (dashboard redirects here)
 * Logo links here.
 *
 * Zones (single scroll):
 *   1. Greeting + CTA
 *   2. NP arc + stats + Tomorrow card
 *   3. Weekly calendar heatmap
 *   4. Challenges
 *   5. Groups / leaderboard
 *   6. Lifetime stats
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, differenceInMinutes, parseISO } from "date-fns";
import {
  Play, Sun, Lock, Users, Plus, ChevronRight,
  Copy, Check, X, Flame, Calendar, TrendingUp,
  Clock, Loader2, UserPlus, Heart, Bluetooth, BluetoothOff, Activity
} from "lucide-react";
import { Button } from "../components/ui/button";
import { SubscriptionBanner } from "../components/SubscriptionBanner";
import { SLMConsentModal } from "../components/SLMConsentModal";
import { BetaWelcomeBanner } from "../components/BetaWelcomeBanner";
import { useBiofeedbackContext } from "../contexts/BiofeedbackContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Design tokens ──────────────────────────────────────────────────────────
const T = {
  bg:       '#08090b',
  surface:  '#0f1114',
  surf2:    '#161a1f',
  border:   'rgba(255,255,255,0.07)',
  text:     '#e8e2d6',
  muted:    'rgba(237,232,223,0.55)',
  dim:      'rgba(237,232,223,0.32)',
  amber:    '#C9A84C',
  green:    '#4CAF7A',
  blue:     '#4A90D9',
  indigo:   '#5B4FCF',
  orange:   '#D4723A',
  violet:   '#9B59B6',
};

const mono  = { fontFamily: 'DM Mono, monospace' };
const serif = { fontFamily: 'DM Serif Display, Georgia, serif' };
const label = (color = T.dim) => ({ ...mono, fontSize: 9, letterSpacing: '0.18em',
  textTransform: 'uppercase', color });

// ── Helpers ────────────────────────────────────────────────────────────────
const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

const formatWake = (tw) =>
  tw.beforeWake ? '--:--'
  : `${String(tw.hours).padStart(2,'0')}:${String(tw.minutes).padStart(2,'0')}`;

const getWakeNudge = (completedToday, tw) => {
  if (completedToday) return 'Protocol complete today.';
  if (tw.beforeWake)  return 'Your wake time has not arrived yet.';
  if (tw.hours === 0 && tw.minutes < 30) return 'Perfect window. Start now.';
  if (tw.hours < 2)  return 'Still in your optimal window.';
  return "It's never too late. Begin your protocol.";
};

const streakMult = (s) => s >= 21 ? 1.5 : s >= 14 ? 1.4 : s >= 7 ? 1.3 : s >= 3 ? 1.2 : 1.0;

// ── NP Arc (canvas) ────────────────────────────────────────────────────────
const NPArc = ({ pct, np }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const W = 130, cx = 65, cy = 65, r = 54;
    ctx.clearRect(0, 0, W, W);
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI/2, Math.PI*2 - Math.PI/2);
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ctx.lineWidth = 5;
    ctx.stroke();
    if (pct > 0) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, -Math.PI/2, (Math.PI*2*Math.min(pct,1)) - Math.PI/2);
      ctx.strokeStyle = T.amber;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }, [pct]);
  return (
    <div style={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
      <canvas ref={ref} width={130} height={130} style={{ width: 130, height: 130 }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ ...mono, fontSize: 24, color: T.text, lineHeight: 1 }}>{np.toLocaleString()}</span>
        <span style={{ ...label(T.amber), marginTop: 3 }}>Lifetime NP</span>
      </div>
    </div>
  );
};

// ── Tomorrow card ──────────────────────────────────────────────────────────
const TomorrowCard = ({ proj }) => {
  if (!proj) return null;
  const { completed_today, projected_gain_pct, current_streak,
          tomorrow_multiplier, current_multiplier, streak_at_risk } = proj;

  if (completed_today) return (
    <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
      <div style={{ ...label(T.amber), marginBottom: 6 }}>See you tomorrow</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 6 }}>
            Complete tomorrow's protocol to earn
          </p>
          {streak_at_risk && (
            <p style={{ ...mono, fontSize: 10, color: 'rgba(237,232,223,0.4)' }}>
              Streak ×{current_multiplier} → ×{tomorrow_multiplier} if you return tomorrow
            </p>
          )}
        </div>
        <div style={{ ...mono, fontSize: 22, color: T.amber, flexShrink: 0 }}>
          +{projected_gain_pct}%
        </div>
      </div>
    </div>
  );

  // Not completed yet today
  return (
    <div style={{ background: 'rgba(91,79,207,0.06)', border: '1px solid rgba(91,79,207,0.2)',
      borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
      <div style={{ ...label('#5B4FCF'), marginBottom: 6 }}>Projected today</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
          Complete today's protocol to gain
          {current_streak > 0 ? ` (streak ×${current_multiplier})` : ''}
        </p>
        <div style={{ ...mono, fontSize: 22, color: '#5B4FCF', flexShrink: 0 }}>
          +{projected_gain_pct}%
        </div>
      </div>
      {current_streak >= 3 && (
        <p style={{ ...mono, fontSize: 10, color: 'rgba(212,114,58,0.8)', marginTop: 6 }}>
          Missing today resets your ×{current_multiplier} streak bonus to ×1.0
        </p>
      )}
    </div>
  );
};

// ── Weekly heatmap ─────────────────────────────────────────────────────────
const WeekHeatmap = ({ days }) => {
  const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const today = format(new Date(), 'yyyy-MM-dd');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
      {(days || []).map((d, i) => {
        const isToday = d.date === today;
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ ...mono, fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: T.dim }}>{DAYS[i]}</span>
            <div style={{
              width: 32, height: 32, borderRadius: 5,
              background: d.completed ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${d.completed ? 'rgba(201,168,76,0.4)' : isToday ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)'}`,
              boxShadow: isToday ? '0 0 0 2px rgba(201,168,76,0.25)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {d.completed && <span style={{ fontSize: 11, color: T.amber }}>✓</span>}
              {!d.completed && isToday && <span style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)' }}>·</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Stat mini-card ─────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, iconColor, label: lbl, value, sub }) => (
  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
    padding: '14px 14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <Icon style={{ width: 13, height: 13, color: iconColor }} />
      <span style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: T.dim }}>{lbl}</span>
    </div>
    <p style={{ ...mono, fontSize: 22, color: T.text, lineHeight: 1 }}>{value}</p>
    {sub && <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 3 }}>{sub}</p>}
  </div>
);

// ── Challenge bar ──────────────────────────────────────────────────────────
const ChallengeCard = ({ icon, title, sub, progress, target }) => {
  const pct = Math.min(100, (progress / target) * 100);
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 15, marginBottom: 6 }}>{icon}</div>
      <p style={{ fontSize: 12, color: T.text, marginBottom: 2 }}>{title}</p>
      <p style={{ fontSize: 10, color: T.dim, marginBottom: 8, fontWeight: 300 }}>{sub}</p>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginBottom: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: T.amber, borderRadius: 2 }} />
      </div>
      <span style={{ ...mono, fontSize: 9, color: T.dim }}>{progress} / {target}</span>
    </div>
  );
};

// ── Group leaderboard ──────────────────────────────────────────────────────
const Leaderboard = ({ rows, weekStart }) => {
  const rankColors = [T.amber, 'rgba(237,232,223,0.6)', 'rgba(212,114,58,0.8)'];
  const avatarColors = [T.indigo, T.orange, T.green, T.blue, T.violet, T.amber];
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 56px 72px',
        gap: 8, padding: '8px 14px', borderBottom: `1px solid ${T.border}` }}>
        {['#','Member','Sessions','Gain'].map((h,i) => (
          <span key={h} style={{ ...mono, fontSize: 8, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: T.dim,
            textAlign: i >= 2 ? 'right' : 'left' }}>{h}</span>
        ))}
      </div>
      {rows.map((r, i) => (
        <div key={r.user_id} style={{
          display: 'grid', gridTemplateColumns: '24px 1fr 56px 72px',
          gap: 8, padding: '10px 14px', alignItems: 'center',
          borderBottom: `1px solid rgba(255,255,255,0.04)`,
          background: r.is_me ? 'rgba(201,168,76,0.04)' : 'transparent',
          borderLeft: r.is_me ? '2px solid rgba(201,168,76,0.35)' : '2px solid transparent',
        }}>
          <span style={{ ...mono, fontSize: 11, color: rankColors[i] || T.dim,
            textAlign: 'center' }}>{r.rank}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: `${avatarColors[i % avatarColors.length]}22`,
              color: avatarColors[i % avatarColors.length],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 500 }}>
              {r.name.split(' ').map(n => n[0]).join('').slice(0,2)}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 12, color: T.text, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.name}{r.is_me && <span style={{ ...mono, fontSize: 8,
                  color: T.amber, marginLeft: 5 }}>you</span>}
              </p>
            </div>
          </div>
          <span style={{ ...mono, fontSize: 11, color: T.muted, textAlign: 'right' }}>
            {r.sessions}/7
          </span>
          <span style={{ ...mono, fontSize: 12, color: T.green, textAlign: 'right' }}>
            +{r.weekly_gain}%
          </span>
        </div>
      ))}
      {weekStart && (
        <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ ...mono, fontSize: 9, color: T.dim }}>Resets Monday 00:00</span>
        </div>
      )}
    </div>
  );
};

// ── Groups section ─────────────────────────────────────────────────────────
const GroupsSection = ({ user }) => {
  const [groups,         setGroups]         = useState([]);
  const [activeGroupId,  setActiveGroupId]  = useState(null);
  const [leaderboard,    setLeaderboard]    = useState(null);
  const [loadingLB,      setLoadingLB]      = useState(false);
  const [showCreate,     setShowCreate]     = useState(false);
  const [showJoin,       setShowJoin]       = useState(false);
  const [newGroupName,   setNewGroupName]   = useState('');
  const [joinCode,       setJoinCode]       = useState('');
  const [saving,         setSaving]         = useState(false);
  const [copiedId,       setCopiedId]       = useState(null);

  const loadGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`, { withCredentials: true });
      const gs = res.data.groups || [];
      setGroups(gs);
      if (gs.length > 0 && !activeGroupId) {
        setActiveGroupId(gs[0].group_id);
      }
    } catch {}
  }, [activeGroupId]);

  useEffect(() => { loadGroups(); }, []);

  useEffect(() => {
    if (!activeGroupId) return;
    setLoadingLB(true);
    axios.get(`${API}/groups/${activeGroupId}/leaderboard`, { withCredentials: true })
      .then(r => setLeaderboard(r.data))
      .catch(() => setLeaderboard(null))
      .finally(() => setLoadingLB(false));
  }, [activeGroupId]);

  const handleCreate = async () => {
    if (!newGroupName.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/groups`, { name: newGroupName.trim() },
        { withCredentials: true });
      toast.success(`Group "${res.data.name}" created`);
      setNewGroupName(''); setShowCreate(false);
      await loadGroups();
      setActiveGroupId(res.data.group_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create group');
    } finally { setSaving(false); }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/groups/join`, { invite_code: joinCode.trim() },
        { withCredentials: true });
      if (res.data.already_member) toast.info('Already a member of this group');
      else toast.success(`Joined "${res.data.name}"`);
      setJoinCode(''); setShowJoin(false);
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid invite code');
    } finally { setSaving(false); }
  };

  const copyInvite = (group) => {
    navigator.clipboard.writeText(group.invite_url || group.invite_code).catch(() => {});
    setCopiedId(group.group_id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const activeGroup = groups.find(g => g.group_id === activeGroupId);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ ...label(), fontSize: 9 }}>Groups</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { setShowJoin(j => !j); setShowCreate(false); }}
            style={{ ...mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}`,
              color: T.dim, padding: '5px 10px', borderRadius: 4, cursor: 'pointer' }}>
            Join
          </button>
          <button onClick={() => { setShowCreate(c => !c); setShowJoin(false); }}
            style={{ ...mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: `rgba(201,168,76,0.1)`, border: `1px solid rgba(201,168,76,0.3)`,
              color: T.amber, padding: '5px 10px', borderRadius: 4, cursor: 'pointer' }}>
            + Create
          </button>
        </div>
      </div>

      {/* Create panel */}
      {showCreate && (
        <div style={{ background: T.surface, border: `1px solid rgba(201,168,76,0.2)`,
          borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <p style={{ ...label(T.amber), marginBottom: 8 }}>New group</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Shim Circle"
              style={{ flex: 1, background: T.surf2, border: `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 6, padding: '7px 10px', fontSize: 13, color: T.text,
                outline: 'none' }} />
            <button onClick={handleCreate} disabled={saving || !newGroupName.trim()}
              style={{ background: T.amber, color: '#0a0c0f', border: 'none', borderRadius: 6,
                padding: '7px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                opacity: saving ? 0.6 : 1 }}>
              {saving ? '...' : 'Create'}
            </button>
          </div>
          <p style={{ fontSize: 10, color: T.dim, marginTop: 6 }}>
            Max 10 members per group · max 5 groups per account
          </p>
        </div>
      )}

      {/* Join panel */}
      {showJoin && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <p style={{ ...label(), marginBottom: 8 }}>Join with invite code</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={joinCode} onChange={e => setJoinCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleJoin()}
              placeholder="8-character code"
              style={{ flex: 1, background: T.surf2, border: `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 6, padding: '7px 10px', fontSize: 13, color: T.text,
                outline: 'none', ...mono }} />
            <button onClick={handleJoin} disabled={saving || !joinCode.trim()}
              style={{ background: T.surf2, color: T.text, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                opacity: saving ? 0.6 : 1 }}>
              {saving ? '...' : 'Join'}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {groups.length === 0 && !showCreate && !showJoin && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
          padding: '28px 20px', textAlign: 'center' }}>
          <Users style={{ width: 28, height: 28, color: 'rgba(255,255,255,0.15)',
            margin: '0 auto 10px' }} />
          <p style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>No groups yet</p>
          <p style={{ fontSize: 11, color: T.dim, lineHeight: 1.6 }}>
            Create a group and invite colleagues. Weekly leaderboard resets every Monday.
          </p>
        </div>
      )}

      {/* Group switcher tabs */}
      {groups.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 2, background: T.surface,
            border: `1px solid ${T.border}`, borderRadius: 6, padding: 3, marginBottom: 12 }}>
            {groups.map(g => (
              <button key={g.group_id} onClick={() => setActiveGroupId(g.group_id)}
                style={{ flex: 1, textAlign: 'center', ...mono, fontSize: 9,
                  letterSpacing: '0.1em', textTransform: 'uppercase', padding: '6px 4px',
                  borderRadius: 4, cursor: 'pointer', border: 'none', transition: 'all .15s',
                  background: activeGroupId === g.group_id ? 'rgba(255,255,255,0.07)' : 'transparent',
                  color: activeGroupId === g.group_id ? T.text : T.dim }}>
                {g.name}
              </button>
            ))}
          </div>

          {/* Invite link for active group */}
          {activeGroup && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1, background: T.surf2, border: `1px solid ${T.border}`,
                borderRadius: 4, padding: '6px 10px', ...mono, fontSize: 10,
                color: T.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeGroup.invite_url || `clarity-os.co/join/${activeGroup.invite_code}`}
              </div>
              <button onClick={() => copyInvite(activeGroup)}
                style={{ background: copiedId === activeGroup.group_id
                  ? 'rgba(76,175,122,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${copiedId === activeGroup.group_id
                    ? 'rgba(76,175,122,0.3)' : T.border}`,
                  borderRadius: 4, padding: '6px 10px', cursor: 'pointer',
                  color: copiedId === activeGroup.group_id ? T.green : T.dim,
                  ...mono, fontSize: 9, letterSpacing: '0.1em',
                  textTransform: 'uppercase', transition: 'all .2s', flexShrink: 0 }}>
                {copiedId === activeGroup.group_id ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          )}

          {/* Leaderboard */}
          {loadingLB ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <Loader2 style={{ width: 18, height: 18, color: T.dim,
                animation: 'spin 1s linear infinite' }} />
            </div>
          ) : leaderboard ? (
            <Leaderboard rows={leaderboard.members} weekStart={leaderboard.week_start} />
          ) : null}
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg);}} @keyframes hrPulse{0%,100%{opacity:0.5;transform:scale(1);}50%{opacity:1;transform:scale(1.4);}}`}</style>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export const Progress = ({ user }) => {
  const navigate = useNavigate();

  // Inherited Dashboard state
  const [stats,             setStats]             = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [timeSinceWake,     setTimeSinceWake]     = useState({ hours: 0, minutes: 0 });
  const [completedToday,    setCompletedToday]    = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [subscription,      setSubscription]      = useState(null);
  const [showSLMModal,      setShowSLMModal]      = useState(false);
  const [showBetaBanner,    setShowBetaBanner]    = useState(false);
  const [currentUser,       setCurrentUser]       = useState(user);

  // Progress state
  const [progressData,  setProgressData]  = useState(null);
  const [projection,    setProjection]    = useState(null);
  const [baselineData,  setBaselineData]  = useState(null);

  // Biofeedback — instantiated here so it persists across sessions and auto-reconnects
  const bio = useBiofeedbackContext();

  // ── Data fetching ──────────────────────────────────────────────────────
  useEffect(() => {
    fetchAll();
    if (user?.slm_training_consent === undefined) setShowSLMModal(true);
    if (user?.beta_mode && !user?.beta_welcome_dismissed) setShowBetaBanner(true);
  }, [user]);

  // Wake clock
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const [h, m] = (user?.wake_time || '06:00').split(':').map(Number);
      const wake = new Date(); wake.setHours(h, m, 0, 0);
      if (now < wake) return setTimeSinceWake({ hours: 0, minutes: 0, beforeWake: true });
      const diff = differenceInMinutes(now, wake);
      setTimeSinceWake({ hours: Math.floor(diff / 60), minutes: diff % 60, beforeWake: false });
    };
    calc();
    const iv = setInterval(calc, 60000);
    return () => clearInterval(iv);
  }, [user?.wake_time]);

  const fetchAll = async () => {
    try {
      const [statsRes, subRes, notifRes, projRes, baselineRes] = await Promise.allSettled([
        axios.get(`${API}/completions/stats`,              { withCredentials: true }),
        axios.get(`${API}/subscription/status`,            { withCredentials: true }),
        axios.get(`${API}/notifications/status`,           { withCredentials: true }),
        axios.get(`${API}/progress/tomorrow-projection`,   { withCredentials: true }),
        axios.get(`${API}/baseline`,                       { withCredentials: true }),
      ]);

      if (statsRes.status === 'fulfilled') {
        const s = statsRes.value.data;
        setStats(s);
        const today = format(new Date(), 'yyyy-MM-dd');
        setCompletedToday(s.last_7_days?.find(d => d.date === today)?.completed || false);

        // Compute progress values
        const totalDays   = s.total_days || 0;
        const streak      = s.current_streak || 0;
        const weekSessions= s.last_7_days?.filter(d => d.completed).length || 0;
        const lifetimeNP  = Math.round(totalDays * 4.7 * 10 + 500);
        const milestones  = [100, 250, 500, 1000, 2500, 5000];
        const nxt         = milestones.find(m => m > lifetimeNP) || 5000;
        const prv         = milestones[milestones.indexOf(nxt) - 1] || 0;
        const mult        = streakMult(streak);
        const todayDelta  = completedToday ? +(3.0 * (72/70) * mult).toFixed(1) : 0;

        setProgressData({
          lifetimeNP,
          lifetimeProgress: Math.min(1, (lifetimeNP - prv) / (nxt - prv)),
          weekProgress:     weekSessions / 7,
          todayDelta,
          streak,
          mult: mult.toFixed(1),
          totalDays,
          weekSessions,
          consistency30: s.last_30_days
            ? Math.round((s.last_30_days.filter(d => d.completed).length / 30) * 100)
            : 0,
          badges: s.badges || [],
          last7: s.last_7_days || [],
        });
      }
      if (subRes.status === 'fulfilled')   setSubscription(subRes.value.data);
      if (notifRes.status === 'fulfilled') setNotificationsEnabled(notifRes.value.data.subscribed);
      if (projRes.status === 'fulfilled')  setProjection(projRes.value.data);
      if (baselineRes.status === 'fulfilled') {
        const baselines = baselineRes.value.data || [];
        if (baselines.length > 0) setBaselineData(baselines[0]);
      }

    } catch (e) {
      console.error('fetchAll error', e);
    } finally {
      setLoading(false);
    }
  };

  const enableNotifications = async () => {
    try {
      if (!('Notification' in window)) { toast.error('Notifications not supported'); return; }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Please enable notifications in browser settings'); return; }
      await axios.post(`${API}/notifications/subscribe`,
        { endpoint: 'browser-notification', keys: { wake_time: user?.wake_time || '06:00' } },
        { withCredentials: true });
      setNotificationsEnabled(true);
      toast.success('Morning notifications enabled');
    } catch { toast.error('Failed to enable notifications'); }
  };

  // ── Loading ────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh' }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${T.amber}`,
        borderTopColor: 'transparent', borderRadius: '50%',
        animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );

  const pd = progressData || {};

  return (
    <div style={{ paddingBottom: 80 }} data-testid="progress-page">

      {/* Modals */}
      {showSLMModal && (
        <SLMConsentModal user={currentUser}
          onConsent={() => { setShowSLMModal(false); setCurrentUser(p => ({...p, slm_training_consent: true})); }}
          onDecline={() => { setShowSLMModal(false); setCurrentUser(p => ({...p, slm_training_consent: false})); }}
        />
      )}
      {showBetaBanner && (
        <BetaWelcomeBanner user={currentUser}
          onDismiss={() => { setShowBetaBanner(false); setCurrentUser(p => ({...p, beta_welcome_dismissed: true})); }}
        />
      )}

      {/* Subscription banner */}
      <SubscriptionBanner subscription={subscription} />

      {/* ── ZONE 1: Greeting + CTA ──────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ color: 'rgba(156,164,190,1)', fontSize: 13, fontWeight: 300, marginBottom: 4 }}>
          Good {getTimeOfDay()},
        </p>
        <h1 style={{ ...serif, fontSize: 32, fontWeight: 400, color: T.text, marginBottom: 16 }}>
          {user?.name?.split(' ')[0] || 'Practitioner'}
        </h1>

        {/* Wake row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            background: '#161a1f', border: `1px solid ${T.border}`,
            borderRadius: 4, padding: '10px 14px' }}>
            <Sun style={{ width: 16, height: 16, color: T.amber }} />
            <div>
              <p style={{ ...label(), marginBottom: 2 }}>Time Since Wake</p>
              <p style={{ ...mono, fontSize: 18, color: T.text }}
                data-testid="time-since-wake">{formatWake(timeSinceWake)}</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 300 }}>
              {getWakeNudge(completedToday, timeSinceWake)}
            </p>
            {!notificationsEnabled && (
              <button onClick={enableNotifications}
                style={{ fontSize: 12, color: T.blue, background: 'none', border: 'none',
                  cursor: 'pointer', padding: 0, marginTop: 4 }}>
                Enable morning reminders →
              </button>
            )}
          </div>
        </div>

        {/* CTA */}
        {subscription && !subscription.can_access_protocol ? (
          <button onClick={() => navigate('/upgrade')} data-testid="upgrade-to-continue-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.amber, color: '#0a0c0f', padding: '13px 22px',
              borderRadius: 8, fontSize: 14, fontWeight: 500, border: 'none', cursor: 'pointer' }}>
            <Lock style={{ width: 16, height: 16 }} />
            Upgrade to Continue
          </button>
        ) : (
          <button onClick={() => navigate('/protocol')}
            disabled={completedToday}
            data-testid="start-protocol-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
              background: completedToday ? 'rgba(201,168,76,0.15)' : T.amber,
              color: completedToday ? T.amber : '#0a0c0f',
              padding: '13px 22px', borderRadius: 8, fontSize: 14, fontWeight: 500,
              border: completedToday ? `1px solid rgba(201,168,76,0.3)` : 'none',
              cursor: completedToday ? 'default' : 'pointer',
              opacity: 1, transition: 'opacity .15s' }}>
            <Play style={{ width: 16, height: 16 }} />
            {completedToday ? 'Protocol complete today' : 'Begin Protocol'}
          </button>
        )}
      </div>

      {/* ── ZONE 2: NP arc + stats + Tomorrow ───────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <NPArc pct={pd.lifetimeProgress || 0} np={pd.lifetimeNP || 0} />
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              <div>
                <p style={{ ...mono, fontSize: 18,
                  color: completedToday ? T.green : T.text }}>
                  {completedToday ? `+${pd.todayDelta}%` : '+0.0%'}
                </p>
                <p style={{ ...label(), marginTop: 2 }}>Today</p>
              </div>
              <div>
                <p style={{ ...mono, fontSize: 18, color: T.amber }}>×{pd.mult || '1.0'}</p>
                <p style={{ ...label(), marginTop: 2 }}>Streak bonus</p>
              </div>
              <div>
                <p style={{ ...mono, fontSize: 18,
                  color: completedToday ? T.green : T.text }}>
                  {completedToday ? '100%' : '0%'}
                </p>
                <p style={{ ...label(), marginTop: 2 }}>Completion</p>
              </div>
              <div>
                <p style={{ ...mono, fontSize: 18,
                  color: bio.isConnected ? (bio.hrv ? T.green : T.blue) : T.dim }}>
                  {bio.isConnected
                    ? (bio.hrv ? `${bio.hrv} ms` : bio.heartRate ? `${bio.heartRate} bpm` : '...')
                    : '—'}
                </p>
                <p style={{ ...label(), marginTop: 2 }}>
                  {bio.isConnected ? 'HRV live' : 'HRV — connect below'}
                </p>
              </div>
            </div>
          </div>
        </div>
        <TomorrowCard proj={projection} />
      </div>

      {/* ── ZONE 2b: Breath Capacity ─────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 18, marginBottom: 14 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ ...label(T.amber) }}>BREATH CAPACITY</span>
          <button
            onClick={() => navigate('/baseline')}
            style={{ ...mono, fontSize: 10, color: T.amber, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Update →
          </button>
        </div>

        {baselineData ? (
          <>
            {/* Score display */}
            <div style={{ marginBottom: 16 }}>
              <span style={{ ...mono, fontSize: 32, color: T.text }}>
                {baselineData.composite_score || Math.round(
                  ((baselineData.practice_scores?.kapalbhati || 0) +
                   (baselineData.practice_scores?.nadi_shodhana || 0) +
                   (baselineData.practice_scores?.bhramari || 0)) / 3
                )}
              </span>
              <span style={{ ...mono, fontSize: 14, color: T.dim }}> /100</span>
              <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 4 }}>
                Last measured {baselineData.timestamp 
                  ? format(parseISO(baselineData.timestamp), 'MMM d, yyyy')
                  : 'recently'}
              </p>
            </div>

            {/* Practice bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { name: 'Kapalbhati', key: 'kapalbhati', color: T.blue },
                { name: 'Nadi Shodhana', key: 'nadi_shodhana', color: T.blue },
                { name: 'Bhramari', key: 'bhramari', color: T.violet },
              ].map(p => {
                const score = baselineData.practice_scores?.[p.key] || 0;
                return (
                  <div key={p.key}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ ...serif, fontSize: 13, color: T.text }}>{p.name}</span>
                      <span style={{ ...mono, fontSize: 11, color: T.muted }}>{score} <span style={{ color: T.dim }}>/100</span></span>
                    </div>
                    <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${score}%`, background: p.color, borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          /* No baseline data */
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <p style={{ fontSize: 12, color: T.dim, marginBottom: 12 }}>No baseline recorded yet.</p>
            <button
              onClick={() => navigate('/baseline')}
              style={{
                ...mono, fontSize: 11, letterSpacing: '0.08em',
                color: '#1a1200', background: T.amber,
                border: 'none', borderRadius: 6, padding: '10px 18px',
                cursor: 'pointer', transition: 'opacity 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={e => e.currentTarget.style.opacity = '1'}
            >
              Run check — ~2 min →
            </button>
          </div>
        )}
      </div>

      {/* ── ZONE 3: Weekly heatmap ───────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ ...label(), marginBottom: 10 }}>This week</p>
        <WeekHeatmap days={pd.last7} />
      </div>

      {/* ── ZONE 4: Challenges ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ ...label(), marginBottom: 10 }}>Challenges</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {(pd.badges || []).map(b => (
            <ChallengeCard key={b.id} icon={b.icon === 'crown' ? '◉' : '◈'}
              title={b.name} sub={b.description}
              progress={b.progress} target={b.target} />
          ))}
        </div>
      </div>

      {/* ── ZONE 5: Groups ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <GroupsSection user={user} />
      </div>

      {/* ── ZONE 6: Biofeedback (HRV) ───────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ ...label(), marginBottom: 10 }}>Biofeedback</p>
        <div style={{ background: T.surface, border: `1px solid ${bio.isConnected ? 'rgba(74,144,217,0.3)' : T.border}`,
          borderRadius: 10, padding: 16, transition: 'border-color .3s' }}>

          {/* Status row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: bio.isConnected ? 16 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Status dot */}
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: bio.isConnected ? T.green : bio.isConnecting || bio.isReconnecting ? T.amber : 'rgba(255,255,255,0.2)',
                boxShadow: bio.isConnected ? `0 0 6px ${T.green}80` : 'none',
                flexShrink: 0,
                animation: bio.isConnecting || bio.isReconnecting ? 'hrPulse 1.2s ease-in-out infinite' : 'none',
              }} />
              <div>
                <p style={{ fontSize: 13, color: T.text, fontWeight: 300 }}>
                  {bio.isConnected
                    ? bio.deviceName || 'Heart Rate Monitor'
                    : bio.isReconnecting
                      ? 'Reconnecting...'
                      : bio.isConnecting
                        ? 'Connecting...'
                        : 'No device connected'}
                </p>
                {bio.error && (
                  <p style={{ ...mono, fontSize: 10, color: 'rgba(217,74,74,0.8)', marginTop: 2 }}>
                    {bio.error}
                  </p>
                )}
                {!bio.isConnected && !bio.error && !bio.isConnecting && !bio.isReconnecting && bio.isSupported && (
                  <p style={{ fontSize: 11, color: T.dim, marginTop: 2, fontWeight: 300 }}>
                    Pair once — reconnects automatically each visit
                  </p>
                )}
                {!bio.isSupported && (
                  <p style={{ fontSize: 11, color: T.dim, marginTop: 2, fontWeight: 300 }}>
                    Web Bluetooth not supported in this browser
                  </p>
                )}
              </div>
            </div>

            {/* Connect / disconnect button */}
            {bio.isSupported && (
              bio.isConnected ? (
                <button onClick={bio.disconnect}
                  style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.1)`,
                    color: T.dim, padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={async () => {
                    const ok = await bio.connect();
                    if (!ok) toast.error('Could not connect to HR monitor');
                  }}
                  disabled={bio.isConnecting || bio.isReconnecting}
                  style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: bio.isConnecting || bio.isReconnecting
                      ? 'rgba(74,144,217,0.08)' : 'rgba(74,144,217,0.12)',
                    border: `1px solid rgba(74,144,217,0.3)`,
                    color: bio.isConnecting || bio.isReconnecting ? T.dim : T.blue,
                    padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
                    opacity: bio.isConnecting || bio.isReconnecting ? 0.6 : 1 }}>
                  {bio.isConnecting ? 'Pairing...' : bio.isReconnecting ? 'Reconnecting...' : 'Connect HR monitor'}
                </button>
              )
            )}
          </div>

          {/* Live metrics — only when connected */}
          {bio.isConnected && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {/* Heart rate */}
              <div style={{ background: T.surf2, borderRadius: 8, padding: '12px 10px',
                border: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Heart style={{ width: 11, height: 11, color: 'rgba(217,74,74,0.8)' }} />
                  <span style={{ ...label('rgba(217,74,74,0.7)'), fontSize: 8 }}>Heart rate</span>
                </div>
                <p style={{ ...mono, fontSize: 22, color: T.text, lineHeight: 1 }}>
                  {bio.heartRate ?? '—'}
                </p>
                <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 2 }}>bpm</p>
              </div>

              {/* HRV (RMSSD) */}
              <div style={{ background: T.surf2, borderRadius: 8, padding: '12px 10px',
                border: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Activity style={{ width: 11, height: 11, color: T.blue }} />
                  <span style={{ ...label(T.blue + '99'), fontSize: 8 }}>HRV</span>
                </div>
                <p style={{ ...mono, fontSize: 22, color: bio.hrv ? T.blue : T.dim, lineHeight: 1 }}>
                  {bio.hrv ?? (bio.rrIntervals?.length < 10 ? '...' : '—')}
                </p>
                <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 2 }}>
                  {bio.hrv ? 'ms RMSSD' : bio.rrIntervals?.length < 10 ? `${bio.rrIntervals?.length || 0}/10 RR` : 'ms RMSSD'}
                </p>
              </div>

              {/* HRV quality indicator */}
              <div style={{ background: T.surf2, borderRadius: 8, padding: '12px 10px',
                border: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Bluetooth style={{ width: 11, height: 11, color: T.green }} />
                  <span style={{ ...label(T.green + '80'), fontSize: 8 }}>Signal</span>
                </div>
                <p style={{ ...mono, fontSize: 22, lineHeight: 1,
                  color: bio.hrv
                    ? (bio.hrv > 50 ? T.green : bio.hrv > 25 ? T.amber : 'rgba(217,74,74,0.8)')
                    : T.dim }}>
                  {bio.hrv
                    ? (bio.hrv > 50 ? 'High' : bio.hrv > 25 ? 'Med' : 'Low')
                    : '—'}
                </p>
                <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 2 }}>HRV level</p>
              </div>
            </div>
          )}

          {/* RR interval note — calibrating */}
          {bio.isConnected && !bio.hrv && bio.rrIntervals?.length > 0 && (
            <p style={{ ...mono, fontSize: 9, color: T.dim, marginTop: 10, textAlign: 'center' }}>
              Calibrating — need {Math.max(0, 10 - (bio.rrIntervals?.length || 0))} more RR intervals
            </p>
          )}

        </div>
      </div>

      {/* ── ZONE 7: Lifetime stats ───────────────────────────────────────── */}
      <div>
        <p style={{ ...label(), marginBottom: 10 }}>Lifetime</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <StatCard icon={Flame}       iconColor={T.amber}  label="Current streak"   value={pd.streak || 0}     sub="days" />
          <StatCard icon={Calendar}    iconColor={T.blue}   label="Total days"        value={pd.totalDays || 0}  sub="completed" />
          <StatCard icon={TrendingUp}  iconColor={T.green}  label="This week"         value={`${pd.weekSessions || 0}/7`} sub="days" />
          <StatCard icon={Clock}       iconColor={T.violet} label="Consistency"       value={`${pd.consistency30 || 0}%`} sub="30 days" />
        </div>
      </div>
    </div>
  );
};

export default Progress;
